import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Edit,
  Building2,
  Phone,
  Mail,
  MapPin,
  Plus,
  Briefcase,
  DollarSign,
  Clock,
  PhoneCall,
  FileText,
  Trash2,
  Calendar,
} from 'lucide-react';
import { getBusinessCustomer, updateBusinessCustomer, deleteBusinessCustomer, createJob } from '../lib/api';
import { formatCurrency, formatDate, formatDuration } from '../lib/utils';

const JOB_STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  quoted: 'bg-blue-100 text-blue-700',
  approved: 'bg-purple-100 text-purple-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-green-100 text-green-700',
  invoiced: 'bg-orange-100 text-orange-700',
  paid: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
};

export default function CustomerDetailPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [calls, setCalls] = useState([]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showJobDialog, setShowJobDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [newJob, setNewJob] = useState({
    title: '',
    description: '',
    work_items: [{ description: '', quantity: 1, unit: 'each', unit_price: 0 }],
    gst_rate: 10,
    notes: '',
    due_date: '',
  });
  const [saving, setSaving] = useState(false);

  const loadCustomer = async () => {
    try {
      setLoading(true);
      const response = await getBusinessCustomer(customerId);
      setCustomer(response.data.customer);
      setJobs(response.data.jobs || []);
      setCalls(response.data.calls || []);
      setEditForm(response.data.customer);
    } catch (error) {
      toast.error('Failed to load customer');
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomer();
  }, [customerId]);

  const handleSaveCustomer = async () => {
    if (!editForm.name?.trim()) {
      toast.error('Customer name is required');
      return;
    }
    if (!editForm.phone?.trim()) {
      toast.error('Customer phone number is required');
      return;
    }
    setSaving(true);
    try {
      await updateBusinessCustomer(customerId, editForm);
      toast.success('Customer updated');
      setShowEditDialog(false);
      loadCustomer();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update customer');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await deleteBusinessCustomer(customerId);
      toast.success('Customer deleted');
      navigate('/customers');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete customer');
    }
  };

  const calculateJobTotal = (items, gstRate) => {
    const subtotal = items.reduce((sum, item) => {
      return sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
    }, 0);
    const gst = subtotal * (gstRate / 100);
    return { subtotal, gst, total: subtotal + gst };
  };

  const handleAddWorkItem = () => {
    setNewJob({
      ...newJob,
      work_items: [...newJob.work_items, { description: '', quantity: 1, unit: 'each', unit_price: 0 }],
    });
  };

  const handleRemoveWorkItem = (index) => {
    if (newJob.work_items.length === 1) return;
    setNewJob({
      ...newJob,
      work_items: newJob.work_items.filter((_, i) => i !== index),
    });
  };

  const handleWorkItemChange = (index, field, value) => {
    const items = [...newJob.work_items];
    items[index][field] = value;
    setNewJob({ ...newJob, work_items: items });
  };

  const handleCreateJob = async () => {
    if (!newJob.title.trim()) {
      toast.error('Job title is required');
      return;
    }
    setSaving(true);
    try {
      await createJob({
        customer_id: customerId,
        ...newJob,
      });
      toast.success('Job created successfully');
      setShowJobDialog(false);
      setNewJob({
        title: '',
        description: '',
        work_items: [{ description: '', quantity: 1, unit: 'each', unit_price: 0 }],
        gst_rate: 10,
        notes: '',
        due_date: '',
      });
      loadCustomer();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Card><CardContent className="p-6"><Skeleton className="h-32" /></CardContent></Card>
      </div>
    );
  }

  if (!customer) return null;

  const jobTotals = calculateJobTotal(newJob.work_items, newJob.gst_rate);

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/customers')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      {/* Customer Info Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center">
                <span className="text-2xl font-bold text-teal-600">
                  {customer.name?.charAt(0)?.toUpperCase() || '?'}
                </span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{customer.name}</h1>
                {customer.company && (
                  <div className="flex items-center gap-2 text-slate-600 mt-1">
                    <Building2 className="w-4 h-4" />
                    {customer.company}
                  </div>
                )}
                <div className="flex flex-wrap gap-4 mt-3">
                  {customer.email && (
                    <a href={`mailto:${customer.email}`} className="flex items-center gap-1 text-teal-600 hover:underline">
                      <Mail className="w-4 h-4" />
                      {customer.email}
                    </a>
                  )}
                  {customer.phone && (
                    <a href={`tel:${customer.phone}`} className="flex items-center gap-1 text-teal-600 hover:underline">
                      <Phone className="w-4 h-4" />
                      {customer.phone}
                    </a>
                  )}
                  {customer.address && (
                    <span className="flex items-center gap-1 text-slate-600">
                      <MapPin className="w-4 h-4" />
                      {customer.address}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowEditDialog(true)}>
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button variant="destructive" onClick={handleDeleteCustomer}>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>
          {customer.notes && (
            <div className="mt-4 p-3 bg-slate-50 rounded-lg text-slate-600 text-sm">
              <strong>Notes:</strong> {customer.notes}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="jobs">
        <TabsList>
          <TabsTrigger value="jobs" className="flex items-center gap-2">
            <Briefcase className="w-4 h-4" />
            Jobs ({jobs.length})
          </TabsTrigger>
          <TabsTrigger value="calls" className="flex items-center gap-2">
            <PhoneCall className="w-4 h-4" />
            Calls ({calls.length})
          </TabsTrigger>
        </TabsList>

        {/* Jobs Tab */}
        <TabsContent value="jobs" className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Jobs</h2>
            <Button onClick={() => setShowJobDialog(true)} className="bg-teal-600 hover:bg-teal-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Job
            </Button>
          </div>

          {jobs.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Briefcase className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No jobs yet. Create a job to track work and costs.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {jobs.map((job) => (
                <Card 
                  key={job.job_id} 
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/jobs/${job.job_id}`)}
                  data-testid={`job-card-${job.job_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-mono text-slate-400">{job.job_number}</span>
                          <Badge className={JOB_STATUS_COLORS[job.status]}>
                            {job.status.replace('_', ' ')}
                          </Badge>
                        </div>
                        <h3 className="font-semibold text-slate-900 mt-1">{job.title}</h3>
                        {job.description && (
                          <p className="text-sm text-slate-500 mt-1 line-clamp-1">{job.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            {job.work_items?.length || 0} items
                          </span>
                          {job.due_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Due: {formatDate(job.due_date)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-slate-900">
                          {formatCurrency(job.total_with_gst)}
                        </p>
                        <p className="text-xs text-slate-500">Inc. GST</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Calls Tab */}
        <TabsContent value="calls" className="mt-6">
          <h2 className="text-lg font-semibold mb-4">Related Calls</h2>
          {calls.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <PhoneCall className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No calls linked to this customer yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {calls.map((call) => (
                <Card key={call.call_id} className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/calls/${call.call_id}`)}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{call.summary || 'No summary'}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDuration(call.duration)}
                          </span>
                          <span>{formatDate(call.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Customer Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Name *</Label>
              <Input value={editForm.name || ''} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Email</Label>
                <Input value={editForm.email || ''} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
              </div>
              <div className="grid gap-2">
                <Label>Phone *</Label>
                <Input value={editForm.phone || ''} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Company</Label>
              <Input value={editForm.company || ''} onChange={(e) => setEditForm({ ...editForm, company: e.target.value })} />
            </div>
            <div className="grid gap-2">
              <Label>Address</Label>
              <Input value={editForm.address || ''} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} />
            </div>
            <div className="grid gap-2">
              <Label>Notes</Label>
              <Textarea value={editForm.notes || ''} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveCustomer} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Job Dialog */}
      <Dialog open={showJobDialog} onOpenChange={setShowJobDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Job</DialogTitle>
            <DialogDescription>Create a job for {customer.name}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Job Title *</Label>
              <Input 
                value={newJob.title} 
                onChange={(e) => setNewJob({ ...newJob, title: e.target.value })}
                placeholder="e.g., Plumbing repair, Website development"
                data-testid="job-title-input"
              />
            </div>
            <div className="grid gap-2">
              <Label>Description</Label>
              <Textarea 
                value={newJob.description} 
                onChange={(e) => setNewJob({ ...newJob, description: e.target.value })}
                placeholder="Describe the work to be done..."
                rows={2}
              />
            </div>

            {/* Work Items */}
            <div className="grid gap-2">
              <div className="flex justify-between items-center">
                <Label>Work Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={handleAddWorkItem}>
                  <Plus className="w-3 h-3 mr-1" />
                  Add Item
                </Button>
              </div>
              <div className="space-y-3">
                {newJob.work_items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-start p-3 bg-slate-50 rounded-lg">
                    <div className="col-span-5">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => handleWorkItemChange(index, 'description', e.target.value)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => handleWorkItemChange(index, 'quantity', e.target.value)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        placeholder="Unit"
                        value={item.unit}
                        onChange={(e) => handleWorkItemChange(index, 'unit', e.target.value)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Price"
                        value={item.unit_price}
                        onChange={(e) => handleWorkItemChange(index, 'unit_price', e.target.value)}
                      />
                    </div>
                    <div className="col-span-1 flex items-center justify-center">
                      {newJob.work_items.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" onClick={() => handleRemoveWorkItem(index)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <Card>
              <CardContent className="p-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Subtotal:</span>
                    <span className="font-medium">{formatCurrency(jobTotals.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">GST ({newJob.gst_rate}%):</span>
                    <span className="font-medium">{formatCurrency(jobTotals.gst)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>Total:</span>
                    <span className="text-teal-600">{formatCurrency(jobTotals.total)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>GST Rate (%)</Label>
                <Input 
                  type="number" 
                  value={newJob.gst_rate} 
                  onChange={(e) => setNewJob({ ...newJob, gst_rate: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="grid gap-2">
                <Label>Due Date</Label>
                <Input 
                  type="date" 
                  value={newJob.due_date} 
                  onChange={(e) => setNewJob({ ...newJob, due_date: e.target.value })}
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Notes</Label>
              <Textarea 
                value={newJob.notes} 
                onChange={(e) => setNewJob({ ...newJob, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowJobDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateJob} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="create-job-btn">
              {saving ? 'Creating...' : 'Create Job'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
